import { CALI_TEXT_NUMBERS } from 'constants/caliNumbers'
import { federalEITCTable } from 'constants/federalEITCNumbers'

// Types for eligibility checks
export interface EligibilityConditions {
  usCitizenSsn: boolean
  taxesWithItin: boolean
  livedInUs6Months: boolean
  livedInCa6Months: boolean
  notDependent: boolean
  noInvestmentIncomeOver11k: boolean
  hasChildUnder6: boolean
  fosterCareAfter13: boolean
  spouseSsn?: boolean  // New field for 2026 CTC requirement
}

export interface EligibilityInput {
  conditions: EligibilityConditions
  qualifyingChildren: number
  qualifyingChildrenUnder17: number
  ageOnDec31: number
  filingStatus: string
}

/**
 * Federal EITC Eligibility (7 paths)
 * Path 1: No SSN → $0
 * Path 2: SSN + Investment >$11.6k → $0
 * Path 3: SSN + No investment + Children > 0 → Eligible
 * Path 4: SSN + No investment + No children + Age not 25-64 → $0
 * Path 5: SSN + No investment + No children + Age 25-64 + Is dependent → $0
 * Path 6: SSN + No investment + No children + Age 25-64 + Not dependent + Not lived in US 6mo → $0
 * Path 7: SSN + No investment + No children + Age 25-64 + Not dependent + Lived in US 6mo → Eligible
 */
export const isEligibleForFederalEITC = (input: EligibilityInput): boolean => {
  const { conditions, qualifyingChildren, ageOnDec31 } = input

  // Must have SSN (not ITIN)
  if (!conditions.usCitizenSsn) return false

  // Must not have investment income over $11.6k
  if (!conditions.noInvestmentIncomeOver11k) return false

  // If has qualifying children, eligible
  if (qualifyingChildren > 0) return true

  // No children path: must be 25-64, not dependent, lived in US 6 months
  if (ageOnDec31 >= 25 && ageOnDec31 <= 64 && conditions.notDependent && conditions.livedInUs6Months) {
    return true
  }

  return false
}

/**
 * California EITC Eligibility
 * Requires: (SSN OR ITIN) AND lived in CA 6 months AND no investment >$11k
 * AND (has children OR (not dependent AND age >= 18 AND not married_separately))
 */
export const isEligibleForCalEITC = (input: EligibilityInput): boolean => {
  const { conditions, qualifyingChildren, ageOnDec31, filingStatus } = input

  // Must have SSN or ITIN
  if (!conditions.usCitizenSsn && !conditions.taxesWithItin) return false

  // Must have lived in CA 6 months
  if (!conditions.livedInCa6Months) return false

  // Must not have investment income over $11k
  if (!conditions.noInvestmentIncomeOver11k) return false

  // If has qualifying children, eligible
  if (qualifyingChildren > 0) return true

  // No children path: must not be dependent, age >= 18, not married filing separately
  if (conditions.notDependent && ageOnDec31 >= 18 && filingStatus !== 'married_separately') {
    return true
  }

  return false
}

/**
 * Federal CTC Eligibility (current - no spouse SSN check)
 * Simply requires qualifying children under 17 with SSN
 * The actual amount is calculated by calculateCTC based on income
 */
export const isEligibleForFederalCTC = (input: EligibilityInput): boolean => {
  const { conditions, qualifyingChildrenUnder17 } = input

  // Must have SSN
  if (!conditions.usCitizenSsn) return false

  // Must have qualifying children under 17
  if (qualifyingChildrenUnder17 <= 0) return false

  return true
}

/**
 * Federal CTC Eligibility 2026 (with spouse SSN check)
 * Path 1: No SSN → No Credit
 * Path 2: SSN + No children under 17 → No Credit
 * Path 3: SSN + Children under 17 + Not married filing jointly → Eligible (Single Filing)
 * Path 4: SSN + Children under 17 + Married filing jointly + Spouse no SSN → No Credit
 * Path 5: SSN + Children under 17 + Married filing jointly + Spouse has SSN → Eligible (Married Joint)
 */
export const isEligibleForFederalCTC2026 = (input: EligibilityInput): boolean => {
  const { conditions, qualifyingChildrenUnder17, filingStatus } = input

  // Must have SSN
  if (!conditions.usCitizenSsn) return false

  // Must have qualifying children under 17
  if (qualifyingChildrenUnder17 <= 0) return false

  // If married filing jointly, spouse must also have SSN
  if (filingStatus === 'married') {
    if (!conditions.spouseSsn) return false
  }

  return true
}

export const sanitizeNumber = (input: string): number => {
  if (!input) {
    return 0
  }
  const sanitized = input.replace(/,/g, '')
  return parseFloat(sanitized)
}

export const calculateYCTC = (income: number): number => {
  if (income > 0 && income <= 32900) {
    return 1189
  }
  return 0
}

export const getCalEITCCredits = (income: number): number[] => {
  const range = CALI_TEXT_NUMBERS.find(
    (entry) => income >= entry.atLeast && income <= entry.notOver
  )
  return range ? range.credits : [0, 0, 0, 0]
}

export const normalizeFilingStatus = (
  filingStatus: string
): 'all_others' | 'married' => {
  if (filingStatus === 'married') {
    return 'married'
  }
  return 'all_others'
}

// 2025 Federal CTC Parameters (flat credit, no phase-in)
export const calculateCTC = (income, numChildren, filingStatus) => {
  const FED_CTC_AMOUNT = 2200
  const PHASE_OUT_RATE = 0.05
  const SINGLE_PHASE_OUT_START = 200000
  const SINGLE_PHASE_OUT_END = 240000
  const MARRIED_PHASE_OUT_START = 400000
  const MARRIED_PHASE_OUT_END = 440000

  if (numChildren <= 0) return 0

  const maxCredit = FED_CTC_AMOUNT * numChildren
  const phaseOutStart = filingStatus === 'married'
    ? MARRIED_PHASE_OUT_START
    : SINGLE_PHASE_OUT_START
  const phaseOutEnd = filingStatus === 'married'
    ? MARRIED_PHASE_OUT_END
    : SINGLE_PHASE_OUT_END

  if (income <= phaseOutStart) {
    return maxCredit
  }

  if (income >= phaseOutEnd) {
    return 0
  }

  const reduction = (income - phaseOutStart) * PHASE_OUT_RATE
  return Math.max(0, maxCredit - reduction)
}

export const calculateFederalEITC = (
  income: number,
  filingStatus: string,
  qualifyingChildren: string
): string => {
  let key = 'no_children'
  if (qualifyingChildren === '1') key = 'one_child'
  else if (qualifyingChildren === '2') key = 'two_children'
  else if (qualifyingChildren === '3+') key = 'three_children'

  const normalizedFilingStatus = normalizeFilingStatus(filingStatus)

  const {
    phaseInRate,
    maxIncome,
    flatAmount,
    phaseOutRate,
    phaseOutStart,
    phaseOutEnd,
  } = federalEITCTable[key][normalizedFilingStatus]

  let result: number

  if (income <= maxIncome) {
    result = income * phaseInRate
  } else if (income > maxIncome && income <= phaseOutStart) {
    result = flatAmount
  } else if (income > phaseOutStart && income <= phaseOutEnd) {
    result = Math.max(0.0, flatAmount - (income - phaseOutStart) * phaseOutRate)
  } else {
    result = 0
  }

  return result.toFixed(2)
}
